import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import {
  adminErrorsSelector,
  createAdmin,
  getAdminEdit,
  isLoadingSelectedAdminSelector,
  selectedAdminSelector,
  setAdminsErrors,
  setSelectedAdmin,
  updateAdmin,
} from 'src/store/admins';

import { CustomButton, CustomInput, PageLoader, WhiteCard } from 'src/components';

import './adminsForm.scss';

export const AdminsForm = (props) => {
  const { t } = useTranslation('adminsForm');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const selectedAdmin = useSelector(selectedAdminSelector);
  const isLoading = useSelector(isLoadingSelectedAdminSelector);
  const formError = useSelector(adminErrorsSelector);

  const isEditMode = pathname?.includes('edit');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirmation: '',
  });

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEdit(params?.id));
    }

    return () => {
      dispatch(setSelectedAdmin({}));
      dispatch(setAdminsErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, ...selectedAdmin }));
    }
  }, [isEditMode, selectedAdmin]);

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminsErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(updateAdmin({ id: params?.id, payload: formData, navigate }));
        return;
      }

      await dispatch(createAdmin({ navigate, payload: formData }));
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  return (
    <div className="admins-form">
      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>{t(`admins:${!isEditMode ? 'create' : 'edit'}`)}</h3>
        <form onSubmit={onSubmit}>
          <div className="admins-form__content">
            <CustomInput
              type="email"
              label={t('email')}
              value={formData?.email}
              errorMessage={formError?.email}
              onChange={onChangeInput('email')}
            />
            <CustomInput
              label={t('password')}
              value={formData?.password}
              errorMessage={formError?.password}
              onChange={onChangeInput('password')}
              required={!isEditMode}
            />
            <CustomInput
              label={t('confirmPassword')}
              value={formData?.password_confirmation}
              errorMessage={formError?.password_confirmation}
              onChange={onChangeInput('password_confirmation')}
            />
          </div>
          <CustomButton variant="filled" title={t('title:save')} />
        </form>
      </WhiteCard>
    </div>
  );
};
